"use client";

import { motion } from "framer-motion";
import {
  Users,
  Key,
  Building2,
  Sparkles,
  Crown,
  ArrowRight,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";

/* ── colour tokens ── */
const GOLD = "#D4A017";
const BLUE = "#1e5fa6";

/* ── types ── */
interface SubType {
  id: string;
  title: string;
  image: string;
  icon: React.ReactNode;
  href: string;
}

interface Category {
  label: string;
  accentColor: string;
  badge: string;
  badgeIcon: React.ReactNode;
  description: string;
  subTypes: SubType[];
}

/* ── data ── */
const hopoStandard: Category = {
  label: "Hopo Standard",
  accentColor: BLUE,
  badge: "Popular Choice",
  badgeIcon: <Sparkles className="w-3.5 h-3.5" />,
  description:
    "Affordable living with flexible stay options. Reliable service, budget pricing, and a hassle-free experience for professionals and small families.",
  subTypes: [
    {
      id: "coliving",
      title: "Co-living Spaces",
      image: "/images/types/coliving1.png",
      icon: <Users className="w-4 h-4" />,
      href: "/properties?excludePropertyTypes=private-room,full-unit&maxPrice=3000",
    },
    {
      id: "room",
      title: "Private Rooms",
      image: "/images/types/room1.png",
      icon: <Key className="w-4 h-4" />,
      href: "/properties?excludePropertyTypes=coliving,full-unit&minPrice=2000&maxPrice=6500",
    },
  ],
};

const hopoSelect: Category = {
  label: "Hopo Select",
  accentColor: GOLD,
  badge: "Premium",
  badgeIcon: <Crown className="w-3.5 h-3.5" />,
  description:
    "Curated premium residences in prime locations, styled for elevated urban living.",
  subTypes: [
    {
      id: "apartment",
      title: "Studios, 1 & 2 BHK's",
      image: "/images/types/apartment.png",
      icon: <Building2 className="w-4 h-4" />,
      href: "/properties?excludePropertyTypes=coliving,private-room&minPrice=3500",
    },
  ],
};

/* ── image card ── */
function ImageCard({
  sub,
  accent,
  index,
}: {
  sub: SubType;
  accent: string;
  index: number;
}) {
  return (
    <Link href={sub.href} className="block h-full" title={sub.title}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          delay: 0.2 + index * 0.12,
          type: "spring",
          stiffness: 120,
          damping: 20,
        }}
        whileHover={{ y: -4 }}
        className="group cursor-pointer h-full flex flex-col"
      >
        {/* title heading above image */}
        <h4
          className="font-semibold text-[15px] tracking-wide mb-2 flex items-center gap-2"
          style={{ color: accent }}
        >
          <span
            className="p-1.5 rounded-lg text-white"
            style={{ background: accent }}
          >
            {sub.icon}
          </span>
          {sub.title}
        </h4>

        {/* image */}
        <div className="relative rounded-2xl overflow-hidden min-h-[240px] sm:min-h-[200px] flex-1">
          <Image
            src={sub.image}
            alt={sub.title}
            title={sub.title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 550px"
            className=" transition-transform duration-700 ease-out group-hover:scale-105"
          />

          {/* gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-100" />

          {/* explore text overlay */}
          <div className="absolute bottom-4 right-4 flex items-center gap-1.5 text-white font-medium text-[13px] tracking-wide translate-y-4 opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100">
            Explore Properties{" "}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

/* ── category card ── */
function CategoryCard({
  category,
  delay,
}: {
  category: Category;
  delay: number;
}) {
  const isSingle = category.subTypes.length === 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="relative h-full rounded-[28px] bg-white border border-gray-100 shadow-[0_2px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.08)] transition-shadow duration-500 overflow-hidden"
    >
      {/* top accent line */}
      <div
        className="h-1 w-full"
        style={{
          background: `linear-gradient(90deg, ${category.accentColor}00, ${category.accentColor}, ${category.accentColor}00)`,
        }}
      />

      <div className="p-6 sm:p-8 flex flex-col h-full">
        {/* badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: delay + 0.08 }}
          className="self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase mb-4"
          style={{
            background: `${category.accentColor}0D`,
            color: category.accentColor,
            border: `1px solid ${category.accentColor}20`,
          }}
        >
          {category.badgeIcon}
          {category.badge}
        </motion.div>

        {/* title */}
        <motion.h3
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: delay + 0.1 }}
          className="text-2xl sm:text-3xl font-bold mb-2"
        >
          <span className="text-gray-900">Hopo</span>{" "}
          <span style={{ color: category.accentColor }}>
            {category.label.replace("Hopo ", "")}
          </span>
        </motion.h3>

        {/* description */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: delay + 0.15 }}
          className="text-[14px] sm:text-[15px] text-gray-500 leading-relaxed mb-6 max-w-md"
        >
          {category.description}
        </motion.p>

        {/* image grid */}
        <div
          className={`grid gap-4 sm:gap-3 flex-1 min-h-0 ${
            isSingle ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
          }`}
        >
          {category.subTypes.map((sub, i) => (
            <ImageCard
              key={sub.id}
              sub={sub}
              accent={category.accentColor}
              index={i}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ── main section ── */
export default function ThreeTypes() {
  return (
    <section className="py-16 sm:py-20 md:py-28 relative overflow-hidden bg-[#FAFAFA]">
      {/* soft bg blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.07] blur-3xl"
          style={{ background: GOLD }}
        />
        <div
          className="absolute -bottom-32 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.07] blur-3xl"
          style={{ background: BLUE }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* heading */}
        <div className="text-center mb-14">
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-sm font-semibold tracking-widest uppercase text-gray-400 mb-4"
          >
            Property Types
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl sm:text-[44px] font-bold text-gray-900 leading-tight mb-4"
          >
            Find Your Perfect Space
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-lg text-gray-500 max-w-lg mx-auto"
          >
            Serve different lifestyles with a single, AI-managed network of
            homes.
          </motion.p>
        </div>

        {/* cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <CategoryCard category={hopoStandard} delay={0} />
          <CategoryCard category={hopoSelect} delay={0.15} />
        </div>
      </div>
    </section>
  );
}
